import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactNode, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ApiError } from '@/shared/api';
import { Form } from '@/shared/ui/form';
import { applyFieldErrors } from '@/shared/ui/form/apply-field-errors';
import { notifySuccess } from '@/shared/ui/notify';
import { Button } from '@/shared/ui/primitives/button';
import { changePassword } from '../api/account-api';
import { authErrorMessage } from './auth-errors';
import { type ChangePasswordFormValues, changePasswordFormSchema } from './auth-form-schemas';
import { FormAlert } from './form-alert';
import { TextField } from './text-field';

const EMPTY: ChangePasswordFormValues = { currentPassword: '', newPassword: '', confirmation: '' };

/** Erros de regra da API que pertencem a um campo específico. */
const FIELD_BY_CODE: Readonly<Record<string, keyof ChangePasswordFormValues>> = {
  CURRENT_PASSWORD_INVALID: 'currentPassword',
  PASSWORD_REUSED: 'newPassword',
};

/** AUTH-5: formulário de troca de senha do usuário logado; as outras sessões são encerradas. */
export function ChangePasswordForm(): ReactNode {
  const [failure, setFailure] = useState<string | null>(null);
  const form = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordFormSchema),
    defaultValues: EMPTY,
  });

  async function submit(values: ChangePasswordFormValues): Promise<void> {
    setFailure(null);
    try {
      await changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      form.reset(EMPTY);
      notifySuccess('Senha alterada. As sessões em outros dispositivos foram encerradas.');
    } catch (error) {
      const field =
        error instanceof ApiError && error.code !== undefined
          ? FIELD_BY_CODE[error.code]
          : undefined;
      if (field !== undefined && error instanceof ApiError) {
        form.setError(field, { type: 'server', message: error.detail ?? error.title });
        return;
      }
      if (!applyFieldErrors(error, form.setError, ['currentPassword', 'newPassword'])) {
        setFailure(authErrorMessage(error, 'Não foi possível trocar a senha. Tente de novo.'));
      }
    }
  }

  return (
    <section aria-labelledby="change-password-title" className="max-w-sm space-y-4">
      <div className="space-y-1">
        <h2 id="change-password-title" className="text-lg font-semibold">
          Trocar senha
        </h2>
        <p className="text-sm text-muted-foreground">
          Ao trocar a senha, as sessões abertas em outros dispositivos são encerradas.
        </p>
      </div>
      <Form {...form}>
        <form
          noValidate
          className="space-y-4"
          onSubmit={(event) => {
            void form.handleSubmit(submit)(event);
          }}
        >
          {failure !== null && <FormAlert>{failure}</FormAlert>}
          <TextField
            control={form.control}
            name="currentPassword"
            label="Senha atual"
            type="password"
            autoComplete="current-password"
          />
          <TextField
            control={form.control}
            name="newPassword"
            label="Senha nova"
            type="password"
            autoComplete="new-password"
          />
          <TextField
            control={form.control}
            name="confirmation"
            label="Confirme a senha nova"
            type="password"
            autoComplete="new-password"
          />
          <Button type="submit" disabled={form.formState.isSubmitting}>
            Trocar senha
          </Button>
        </form>
      </Form>
    </section>
  );
}
